const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "devisdeco_admin_token";
const ROLE_KEY = "devisdeco_role";
const PRO_SLUG_KEY = "devisdeco_pro_slug";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ROLE_KEY);
};
export const getRole = () => localStorage.getItem(ROLE_KEY) || "";
export const isSuperadmin = () => getRole() === "superadmin";
export const isOwner = () => getRole() === "owner";

// In-memory marker: true only when a dedicated pro link (/p/:slug) programmatically
// redirected to Home within the CURRENT app session. Reset on any real page reload,
// so a later direct access via quotedesign.app starts with no partner attribution.
let _proRedirect = false;
export const proLink = {
  get: () => localStorage.getItem(PRO_SLUG_KEY) || "",
  set: (slug) => (slug ? localStorage.setItem(PRO_SLUG_KEY, slug) : localStorage.removeItem(PRO_SLUG_KEY)),
  clear: () => localStorage.removeItem(PRO_SLUG_KEY),
  markRedirect: () => { _proRedirect = true; },
  wasRedirect: () => _proRedirect,
};

async function request(path, options = {}, auth = false) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}/api${path}`, { ...options, headers });
  if (!res.ok) {
    let detail = "Une erreur est survenue";
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch (e) {
      /* noop */
    }
    throw new Error(detail);
  }
  return res.json();
}

export const fileUrl = (path) => `${BASE}/api${path}`;
export const fileUrlAuth = (path) => {
  const t = getToken();
  if (!t) return `${BASE}/api${path}`;
  const sep = path.includes("?") ? "&" : "?";
  return `${BASE}/api${path}${sep}token=${encodeURIComponent(t)}`;
};

export const api = {
  createQuote: (payload) =>
    request("/quotes", { method: "POST", body: JSON.stringify({ ...payload, pro_slug: proLink.get() || null }) }),
  getQuote: (id) => request(`/quotes/${id}`),
  publicPro: (slug) => request(`/public/pro/${slug}`),
  adminLogin: async (email, password) => {
    const data = await request("/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(data.access_token);
    if (data.user?.role) localStorage.setItem(ROLE_KEY, data.user.role);
    return data;
  },
  me: () => request("/admin/me", {}, true),
  rootLogin: async (email, password) => {
    const data = await request("/root/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    setToken(data.access_token);
    if (data.user?.role) localStorage.setItem(ROLE_KEY, data.user.role);
    return data;
  },
  transferOwnership: (target_user_id) =>
    request("/root/transfer-ownership", { method: "POST", body: JSON.stringify({ target_user_id }) }, true),
  listUsers: () => request("/admin/users", {}, true),
  createUser: (body) => request("/admin/users", { method: "POST", body: JSON.stringify(body) }, true),
  updateUser: (id, body) => request(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  deleteUser: (id) => request(`/admin/users/${id}`, { method: "DELETE" }, true),
  adminLogout: () => clearToken(),
  hasToken: () => !!getToken(),
  forgotPassword: (email) =>
    request("/admin/forgot", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (email, code, new_password) =>
    request("/admin/reset", {
      method: "POST",
      body: JSON.stringify({ email, code, new_password }),
    }),
  listQuotes: () => request("/admin/quotes", {}, true),
  updateStatus: (id, status) =>
    request(`/admin/quotes/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }, true),
  getPricing: () => request("/admin/pricing", {}, true),
  updatePricing: (pricing) =>
    request("/admin/pricing", { method: "PUT", body: JSON.stringify({ pricing }) }, true),
  resetPricing: () => request("/admin/pricing/reset", { method: "POST" }, true),
  duplicatePricing: (source_id, target_ids) =>
    request("/admin/pricing/duplicate", { method: "POST", body: JSON.stringify({ source_id, target_ids }) }, true),
  addCustomSupply: (payload) =>
    request("/admin/pricing/custom-supply", { method: "POST", body: JSON.stringify(payload) }, true),
  getSettings: () => request("/admin/settings", {}, true),
  updateSettings: (company) =>
    request("/admin/settings", { method: "PUT", body: JSON.stringify(company) }, true),
  uploadLogo: async (file) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetch(`${BASE}/api/admin/logo`, {
      method: "POST",
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Échec de l'upload du logo");
    return res.json();
  },
  uploadLogoFor: async (uid, file) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("uid", uid);
    const res = await fetch(`${BASE}/api/admin/logo`, {
      method: "POST",
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Échec de l'upload du logo");
    return res.json();
  },
  editQuote: (id, body) =>
    request(`/admin/quotes/${id}/edit`, { method: "PATCH", body: JSON.stringify(body) }, true),
  resendQuote: (id) => request(`/admin/quotes/${id}/resend`, { method: "POST" }, true),
  resendWordOwner: (id) => request(`/admin/quotes/${id}/resend-word-owner`, { method: "POST" }, true),
  deleteQuote: (id) => request(`/admin/quotes/${id}`, { method: "DELETE" }, true),
  listSubscriptions: () => request("/admin/subscriptions", {}, true),
  setSubscription: (uid, body) => request(`/admin/users/${uid}/subscription`, { method: "PUT", body: JSON.stringify(body) }, true),
  listPayments: (uid) => request(`/admin/users/${uid}/payments`, {}, true),
  addPayment: (uid, body) => request(`/admin/users/${uid}/payments`, { method: "POST", body: JSON.stringify(body) }, true),
  deletePayment: (uid, pid) => request(`/admin/users/${uid}/payments/${pid}`, { method: "DELETE" }, true),
  listChantiers: () => request("/admin/chantiers", {}, true),
  createChantier: (body) => request("/admin/chantiers", { method: "POST", body: JSON.stringify(body) }, true),
  updateChantier: (id, body) => request(`/admin/chantiers/${id}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  deleteChantier: (id) => request(`/admin/chantiers/${id}`, { method: "DELETE" }, true),
  listPartners: () => request("/admin/partners", {}, true),
  createPartner: async (fields, file) => {
    const token = getToken();
    const form = new FormData();
    form.append("name", fields.name || "");
    form.append("active", fields.active ? "true" : "false");
    form.append("tagline", fields.tagline || "");
    form.append("contact_person", fields.contact_person || "");
    form.append("email", fields.email || "");
    form.append("phone", fields.phone || "");
    form.append("start_date", fields.start_date || "");
    form.append("end_date", fields.end_date || "");
    form.append("negotiated_price", fields.negotiated_price != null ? String(fields.negotiated_price) : "");
    form.append("amount_received", fields.amount_received ? "true" : "false");
    form.append("categories", (fields.categories || []).join(","));
    form.append("countries", (fields.countries || []).join(","));
    form.append("country_pricing", JSON.stringify(fields.country_pricing || {}));
    form.append("file", file, file.name);
    const res = await fetch(`${BASE}/api/admin/partners`, {
      method: "POST",
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Échec de l'ajout du partenaire");
    return res.json();
  },
  updatePartner: (id, body) => request(`/admin/partners/${id}`, { method: "PATCH", body: JSON.stringify(body) }, true),
  updatePartnerLogo: async (id, file) => {
    const token = getToken();
    const form = new FormData();
    form.append("file", file, file.name);
    const res = await fetch(`${BASE}/api/admin/partners/${id}/logo`, {
      method: "POST",
      body: form,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Échec de l'upload du logo");
    return res.json();
  },
  deletePartner: (id) => request(`/admin/partners/${id}`, { method: "DELETE" }, true),
  publicPartners: (category, country) => request(`/public/partners?${category ? `category=${encodeURIComponent(category)}` : ""}${country ? `&country=${encodeURIComponent(country)}` : ""}`),
};

// Local history of quotes the user submitted (client side)
const HISTORY_KEY = "devisdeco_history";
export const history = {
  add: (item) => {
    const list = history.list();
    localStorage.setItem(HISTORY_KEY, JSON.stringify([item, ...list]));
  },
  list: () => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
    } catch (e) {
      return [];
    }
  },
};
